/**
 * SunManager - Handles the sun sphere and its movement
 *
 * The sun follows an arc across the sky based on game time.
 * It fades out at night and fades in during the day.
 *
 * Responsibilities:
 * - Create the sun sphere
 * - Update position based on game time (sunrise -> noon -> sunset)
 * - Fade sun based on ambient light (hide at night)
 * - Update sun color during day/night transitions
 */

import * as THREE from 'three';
import { SUN } from '../config/constants.js';

export class SunManager {
    /**
     * Create a new SunManager
     * @param {THREE.Scene} scene - The Three.js scene to add the sun to
     * @param {THREE.Camera} camera - The camera to follow
     * @param {ScenePreloader} [preloader] - Optional preloader for progress tracking
     */
    constructor(scene, camera, preloader = null) {
        this.scene = scene;
        this.camera = camera;
        this.sun = null;

        // Self-register for preload tracking with dispose callback
        this._onPreloadComplete = preloader?.register('sun', {
            dispose: () => this.dispose()
        });

        // Configuration (from constants)
        this.enabled = SUN.ENABLED;
        this.size = SUN.SIZE;
        this.distance = SUN.DISTANCE;
        this.arcHeight = SUN.ARC_HEIGHT;
        this.fadeThreshold = SUN.FADE_THRESHOLD;

        // Current color (will be updated by color cycle)
        this.color = SUN.DEFAULT_COLOR;

        // Cached objects for per-frame updates (avoid GC pressure)
        this.currentFogColor = new THREE.Color(0xffffff);
        this._cachedBaseColor = new THREE.Color();
        this._cachedFogColor = new THREE.Color(0xffffff);
        this._lastColorHex = null;
        this._lastFogHex = null;

        // Disposal state
        this._disposed = false;
    }

    /**
     * Initialize the sun
     * Call this after scene and camera are ready
     */
    initialize() {
        if (!this.enabled) {
            this._onPreloadComplete?.();
            return;
        }
        this.createSun();
        this._onPreloadComplete?.();
    }

    /**
     * Create the sun sphere with vertical gradient via vertex colors
     * @private
     */
    createSun() {
        const geometry = new THREE.SphereGeometry(this.size, 32, 32);

        // Add vertex colors for gradient effect (lighter at top, darker at bottom)
        const positions = geometry.attributes.position;
        const colors = new Float32Array(positions.count * 3);
        const baseColor = new THREE.Color(this.color);

        for (let i = 0; i < positions.count; i++) {
            const y = positions.getY(i);
            // Normalize Y from -size to +size into 0 to 1
            const gradientT = (y / this.size) * 0.5 + 0.5;
            // Top is full color, bottom is 60% brightness
            const brightness = 0.6 + gradientT * 0.4;

            colors[i * 3] = baseColor.r * brightness;
            colors[i * 3 + 1] = baseColor.g * brightness;
            colors[i * 3 + 2] = baseColor.b * brightness;
        }

        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        const material = new THREE.MeshBasicMaterial({
            vertexColors: true,
            fog: false, // We'll handle fog manually with reduced intensity
            transparent: true,
            opacity: 1.0,
            depthWrite: true
        });

        // Store fog blend amount (5-10% max)
        this.fogBlendAmount = 0.30;

        this.sun = new THREE.Mesh(geometry, material);
        this.sun.renderOrder = 1; // Render after sky and stars so depth test works
        this.scene.add(this.sun);
    }

    /**
     * Update sun position based on game time
     * @param {number} gameTimeNormalized - Normalized game time (0 = midnight, 1 = next midnight)
     * @param {number} ambientIntensity - Current ambient light intensity for fading
     */
    updatePosition(gameTimeNormalized, ambientIntensity) {
        if (!this.sun || !this.enabled) return;

        // Calculate sun arc progress based on game time
        // Sun is above horizon from 6 AM (0.25) to 6 PM (0.75)
        const SUNRISE = 0.25;  // 6 AM
        const SUNSET = 0.75;   // 6 PM
        let sunArcProgress;

        if (gameTimeNormalized >= SUNRISE && gameTimeNormalized <= SUNSET) {
            // Sun is above horizon (6 AM to 6 PM)
            // Map 6 AM-6 PM (0.25-0.75) to full arc 0.0-1.0
            sunArcProgress = (gameTimeNormalized - SUNRISE) / (SUNSET - SUNRISE);
        } else {
            // Sun is below horizon (6 PM to 6 AM)
            // Position sun below horizon on opposite side
            if (gameTimeNormalized > SUNSET) {
                // Evening (6 PM to midnight)
                sunArcProgress = 1.0 + (gameTimeNormalized - SUNSET) / (1.0 - SUNSET) * 0.25;
            } else {
                // Morning (midnight to 6 AM)
                sunArcProgress = -0.25 + (gameTimeNormalized / SUNRISE) * 0.25;
            }
        }

        // Calculate sun position on arc (from behind camera to ahead, through zenith)
        // Arc goes: behind (sunrise in East) -> overhead (noon) -> ahead (sunset in West)
        // Car travels West (-Z direction), so sunset is always ahead
        const sunAngle = Math.PI * sunArcProgress; // 0 to PI for full arc

        // Sun X is fixed at world X=0 (West is a fixed direction)
        // This means as the road curves, the sun will appear off-center
        const x = 0; // Fixed world X position
        const y = Math.sin(sunAngle) * this.arcHeight; // Vertical arc
        const z = Math.cos(sunAngle) * this.distance; // Behind (+Z) to front (-Z)

        this.sun.position.set(
            x, // Fixed X - sun doesn't follow camera's lateral position
            this.camera.position.y + y,
            this.camera.position.z + z
        );

        // Fade sun based on ambient light (hide at night)
        this.updateFade(ambientIntensity);
    }

    /**
     * Update sun fade based on ambient light
     * @param {number} ambientIntensity - Current ambient light intensity
     * @private
     */
    updateFade(ambientIntensity) {
        if (!this.sun) return;

        let targetOpacity = 1.0;

        // With shadow-based lighting, ambient values are:
        // Night: 0.10, Sunset: 0.15, Dawn: 0.18, Day: 0.20
        // Sun should be hidden at night, visible otherwise
        const fadeStart = this.fadeThreshold;      // 0.12 - start fading
        const fadeEnd = this.fadeThreshold + 0.02; // 0.14 - fully visible

        if (ambientIntensity < fadeStart) {
            targetOpacity = 0.0; // Hidden at night
        } else if (ambientIntensity < fadeEnd) {
            // Quick fade between night and dawn/sunset
            targetOpacity = (ambientIntensity - fadeStart) / (fadeEnd - fadeStart);
        }

        this.sun.material.opacity = targetOpacity;
        this.sun.material.transparent = targetOpacity < 1.0;
        this.sun.visible = targetOpacity > 0.01;
    }

    /**
     * Update sun color and vertex gradient (call during day/night transitions)
     * @param {number} color - Hex color for the sun
     */
    setColor(color) {
        // Skip if color hasn't changed
        if (color === this._lastColorHex) return;

        this._lastColorHex = color;
        this.color = color;
        this._updateVertexColors();
    }

    /**
     * Update fog settings and blend into sun color
     * @param {THREE.Color} fogColor - Current fog color
     */
    updateFog(fogColor, fogNear, fogFar) {
        // Skip if fog color hasn't changed (avoid unnecessary vertex updates)
        const fogHex = fogColor.getHex();
        if (fogHex === this._lastFogHex) return;

        this._lastFogHex = fogHex;
        this.currentFogColor.copy(fogColor);
        this._updateVertexColors();
    }

    /**
     * Update vertex colors with gradient and subtle fog blend
     * @private
     */
    _updateVertexColors() {
        if (!this.sun || !this.sun.geometry) return;

        // Use cached color objects (avoid GC pressure)
        this._cachedBaseColor.set(this.color);
        const fogColor = this.currentFogColor;
        const fogBlend = this.fogBlendAmount || 0.08;

        const positions = this.sun.geometry.attributes.position;
        const colors = this.sun.geometry.attributes.color;

        if (colors) {
            const baseR = this._cachedBaseColor.r;
            const baseG = this._cachedBaseColor.g;
            const baseB = this._cachedBaseColor.b;
            const fogR = fogColor.r;
            const fogG = fogColor.g;
            const fogB = fogColor.b;

            for (let i = 0; i < positions.count; i++) {
                const y = positions.getY(i);
                const gradientT = (y / this.size) * 0.5 + 0.5;
                const brightness = 0.6 + gradientT * 0.4;

                // Apply gradient brightness
                let r = baseR * brightness;
                let g = baseG * brightness;
                let b = baseB * brightness;

                // Blend in subtle fog (more at bottom, less at top)
                const fogAmount = fogBlend * (1 - gradientT * 0.5);
                r = r * (1 - fogAmount) + fogR * fogAmount;
                g = g * (1 - fogAmount) + fogG * fogAmount;
                b = b * (1 - fogAmount) + fogB * fogAmount;

                colors.array[i * 3] = r;
                colors.array[i * 3 + 1] = g;
                colors.array[i * 3 + 2] = b;
            }
            colors.needsUpdate = true;
        }
    }

    /**
     * Clean up resources
     */
    dispose() {
        if (this._disposed) return;
        this._disposed = true;

        if (this.sun) {
            this.sun.geometry.dispose();
            this.sun.material.dispose();
            this.scene.remove(this.sun);
            this.sun = null;
        }
    }
}
